import { Button } from '@neo/primitives/button'
import { m } from 'framer-motion'
import { Pause } from 'lucide-react'
import { DURATION, EASE_OUT } from '../motion'
import { useVibeAgentStore } from '../store'
import { resolveGateCountdown, useGateCountdown } from './use-gate-countdown'
import { useVibeAgentApi } from './use-vibe-agent-api'

const REQUEST_CHANGES_TEMPLATE = "I'd like to request some changes."

export const VibeApprovalButtons = () => {
  const gate = useVibeAgentStore((state) => state.gate)
  const userInput = useVibeAgentStore((state) => state.userInput)
  const setUserInput = useVibeAgentStore((state) => state.setUserInput)
  const { approve, holdGateTimer } = useVibeAgentApi()

  const isApprovalGate = gate?.kind === 'approval'

  // The run auto-approves a parked plan after the backend window; show the countdown
  // on the approve button. Display-only — the backend fires the answer. No window
  // (parked) → no countdown.
  const countdownState = resolveGateCountdown(gate?.autoAnswerSeconds)
  const showCountdown = isApprovalGate && countdownState.armed
  const countdown = useGateCountdown(showCountdown, countdownState.seconds)

  const handleRequestChanges = () => {
    if (userInput.includes(REQUEST_CHANGES_TEMPLATE)) return
    const trimmed = userInput.trimEnd()
    const nextInput = trimmed
      ? `${trimmed}\n\n${REQUEST_CHANGES_TEMPLATE}`
      : REQUEST_CHANGES_TEMPLATE
    setUserInput(nextInput)
  }

  if (!isApprovalGate) return null

  return (
    <m.div
      initial={{ opacity: 0, transform: 'translateY(10px)' }}
      animate={{ opacity: 1, transform: 'translateY(0px)' }}
      transition={{ duration: DURATION.minor, ease: EASE_OUT }}
      className="flex justify-end gap-2"
    >
      {showCountdown && countdown > 0 && (
        <Button
          type="button"
          variant="ghost"
          onClick={holdGateTimer}
          className="form-button text-secondary transition-transform duration-150 active:scale-[0.98]"
          aria-label="Stop the auto-approve timer"
          data-testid="button-hold-gate-timer"
        >
          <Pause className="w-3.5 h-3.5 mr-1" />
          Stop timer
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        onClick={handleRequestChanges}
        className="form-button text-secondary transition-transform duration-150 active:scale-[0.98]"
        data-testid="button-make-modifications"
      >
        Request changes
      </Button>
      <Button
        type="button"
        variant="default"
        onClick={approve}
        className="form-button transition-transform duration-150 active:scale-[0.98]"
        data-testid="button-approve-plan"
      >
        {showCountdown && countdown > 0 ? (
          <>
            Approve and continue (<span className="font-jetbrains tabular-nums">{countdown}s</span>)
          </>
        ) : (
          'Approve and continue'
        )}
      </Button>
    </m.div>
  )
}
